import { useState } from 'react'

import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'

import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import FolderSelectorF0 from 'modules/documents/components/Folders/FolderSelectorF0'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { Folder, useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import { useFolderSelectorDialogFix } from './useFolderSelectorDialogFix'

const defineInitialFolder = (docs: Document[], folders: Folder[]): Folder | undefined => {
  const folder = docs[0]?.folder

  if (folder) {
    return folders.find((elem) => elem.id === folder.id) || folders[0]
  }

  return undefined
}

interface Props {
  documents: Document[]
  space: (typeof SPACES)[keyof typeof SPACES]
  onSubmit: (selectedFolderId: string | null) => void
  onClose: () => void
  useF0?: boolean
}

interface F0ModalProps {
  documents: Document[]
  folders: Folder[]
  initialFolder: Folder | undefined
  anyFolder: boolean
  multiDocuments: boolean
  loading: boolean
  selectedFolderId: string | null
  setSelectedFolderId: (id: string | null) => void
  onSubmit: (selectedFolderId: string | null) => void
  onClose: () => void
  filename: string
}

const UpdateCompanyFolderModalF0 = ({
  documents,
  folders,
  initialFolder,
  anyFolder,
  multiDocuments,
  loading,
  selectedFolderId,
  setSelectedFolderId,
  onSubmit,
  onClose,
  filename,
}: F0ModalProps) => {
  const guardedOnClose = useFolderSelectorDialogFix(onClose)

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.tp('documents.employee.bulk-actions.update-folder.title', {
        count: documents.length,
        filename: filename,
      })}
      onClose={guardedOnClose}
      primaryAction={{
        label: i18n.t('documents.employee.bulk-actions.update-folder.submit'),
        onClick: () => onSubmit(selectedFolderId),
        disabled: !anyFolder,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('documents.employee.bulk-actions.update-folder.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        <F0Text
          variant="body"
          content={i18n.tp('documents.employee.bulk-actions.update-folder.description', {
            count: documents.length,
          })}
        />
        {anyFolder && (
          <FolderSelectorF0
            folders={folders}
            initialFolder={initialFolder}
            onChange={setSelectedFolderId}
            noneOption
          />
        )}
        {multiDocuments && (
          <F0Alert
            variant="warning"
            title={i18n.t('documents.employee.bulk-actions.update-folder.alert-datatable')}
            description=""
          />
        )}
      </F0Box>
    </F0Dialog>
  )
}

const UpdateCompanyFolderModal = ({
  documents,
  onSubmit,
  onClose,
  space,
  useF0 = false,
}: Props) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)

  const multiDocuments = useUnsafeMemo(() => {
    return documents.length > 1
  }, [documents])

  const { loading, folders } = useGetFolders({ space })
  const anyFolder = folders.length > 0

  const initialFolder = useUnsafeMemo(() => {
    return folders ? defineInitialFolder(documents, folders) : undefined
  }, [folders, documents])

  useUnsafeEffect(() => {
    if (initialFolder) setSelectedFolderId(initialFolder?.id)
  }, [initialFolder])

  const filename = useUnsafeMemo(() => {
    return documents[0] && documents.length > 0 ? documents[0].filename : ''
  }, [documents])

  if (useF0) {
    return (
      <UpdateCompanyFolderModalF0
        documents={documents}
        folders={folders}
        initialFolder={initialFolder}
        anyFolder={anyFolder}
        multiDocuments={multiDocuments}
        loading={loading}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        onSubmit={onSubmit}
        onClose={onClose}
        filename={filename}
      />
    )
  }

  return (
    <Modal
      illustration="file-move"
      title={i18n.tp('documents.employee.bulk-actions.update-folder.title', {
        count: documents.length,
        filename: filename,
      })}
      description={i18n.tp('documents.employee.bulk-actions.update-folder.description', {
        count: documents.length,
      })}
      onSubmit={() => onSubmit(selectedFolderId)}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('documents.employee.bulk-actions.update-folder.cancel')}
          </Button>
          <Button
            submit
            iconName={loading ? 'folder' : undefined}
            loading={loading}
            disabled={!anyFolder}
          >
            {i18n.t('documents.employee.bulk-actions.update-folder.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        {anyFolder && (
          <FolderSelector
            folders={folders}
            initialFolder={initialFolder}
            label={i18n.t('split_documents.assign_documents.destination_folder')}
            onChange={setSelectedFolderId}
            noneOption
            aria-label={i18n.t('documents.employee.bulk-actions.update-folder.selector')}
          />
        )}
        {multiDocuments && (
          <Alert color="warning">
            {i18n.t('documents.employee.bulk-actions.update-folder.alert-datatable')}
          </Alert>
        )}
      </Box>
    </Modal>
  )
}

export default UpdateCompanyFolderModal
